// ============================================================================
// Chargeur des paquets de règles (lot 1 de la refonte).
//
// `loadRulePack(json)` : schéma strict → lecture des formes → cohérence (variables,
// relations, sens, préconditions, effets) → auto-test numérique de la règle → rejeu
// des exemples. TOUT OU RIEN : un seul défaut (même un exemple) et le paquet entier
// est refusé, avec la liste complète des défauts.
//
// Le paquet chargé est du JSON pur, gelé (Object.freeze en profondeur) : un plugin
// ne peut pas modifier les règles d'un autre après coup.
// ============================================================================

import { decide, factsFrom } from '../conditions';
import { matchRuleStep } from '../match';
import { parseStatement } from '../term';
import {
  ConditionSchema,
  SCALAR_REAL_SORTS,
  type Condition,
  type GoalKind,
  type Sort,
  type StepRelation,
} from '../types';
import {
  flipRelation,
  INEQUALITY_RELATIONS,
  parseExample,
  parseForm,
  type FormRelation,
  type ParsedText,
} from './forms';
import {
  RULE_KIND_RELATION,
  RulePackSchema,
  type OnUnknown,
  type PreconditionKind,
  type RuleKind,
  type RuleSpec,
  type RuleTarget,
} from './schema';
import { checkSemantics, type Sampler, type SemanticResult } from './selftest';

export type RuleIssueCode =
  | 'schema'
  | 'rule_duplicate'
  | 'form_forbidden_char'
  | 'form_forbidden_word'
  | 'form_bad_pattern_var'
  | 'form_literal_symbol'
  | 'form_unparsable'
  | 'form_unsupported_relation'
  | 'form_relation_mismatch'
  | 'form_unused'
  | 'var_undeclared'
  | 'var_unused'
  | 'sort_not_supported'
  | 'applies_to_missing'
  | 'applies_to_mismatch'
  | 'direction_unknown_form'
  | 'direction_same_form'
  | 'direction_duplicate'
  | 'precondition_var_undeclared'
  | 'on_unknown_missing'
  | 'on_unknown_without_preconditions'
  | 'effects_on_identity'
  | 'effect_not_applicable'
  | 'flip_inconsistent'
  | 'rule_unsound'
  | 'rule_untestable'
  | 'example_unparsable'
  | 'example_relation_mismatch'
  | 'example_assume_unknown_var'
  | 'example_failed'
  | 'example_not_refuted'
  | 'example_untestable'
  /** lot 2 : un exemple valide que la règle ne reconnaît pas (forme mal écrite) */
  | 'example_not_recognized'
  /** lot 2 : un exemple invalide que la règle reconnaîtrait comme une application correcte */
  | 'example_wrongly_recognized';

/** Un défaut de règle : code stable + où + détail brut (le texte affiché vient de l'adaptateur). */
export interface RuleIssue {
  code: RuleIssueCode;
  /** identifiant de la règle concernée */
  rule?: string;
  /** chemin dans le JSON (« forms.diff », « examples.1 ») */
  path?: string;
  detail?: string;
}

export interface LoadedForm {
  key: string;
  text: string;
  relation: FormRelation;
  vars: string[];
}
export interface LoadedExample {
  from: string;
  to: string;
  expect: 'valid' | 'invalid';
  assume: Condition[];
}
export interface LoadedRule {
  /** « algebra.difference_of_squares@1 » */
  qid: string;
  id: string;
  version: number;
  /** paquet qualifié : « algebra-core@1 » */
  pack: string;
  kind: RuleKind;
  relation: StepRelation;
  appliesTo: RuleTarget;
  vars: Record<string, Sort>;
  forms: Record<string, LoadedForm>;
  /**
   * `introduces` : variables de la forme d'arrivée absentes de la forme de départ
   * (« multiplier les deux membres par k » : k est choisi par l'élève). Lors d'une
   * vérification elles sont lues sur la ligne écrite ; une recherche doit les choisir.
   */
  directions: { from: string; to: string; goals: GoalKind[]; introduces: string[] }[];
  preconditions: Condition[];
  onUnknown: OnUnknown | null;
  flipRelation: boolean;
  cost: number;
  label: string;
  ref: string | null;
  tags: string[];
  levels: string[];
  maxMatches: number;
  examples: LoadedExample[];
  /** l'auto-test du chargement : une preuve par échantillons, jamais exacte */
  selfTest: { proof: 'sampled'; tested: number };
}
export interface LoadedPack {
  /** « algebra-core@1 » */
  qid: string;
  id: string;
  version: number;
  label: string | null;
  rules: LoadedRule[];
}

export type LoadResult = { ok: true; pack: LoadedPack } | { ok: false; issues: RuleIssue[] };

const DEFAULT_MAX_MATCHES = 50;
const DEFAULT_COST = 1;

/** Gel en profondeur (objets et tableaux). */
export function deepFreeze<T>(x: T): T {
  if (x && typeof x === 'object' && !Object.isFrozen(x)) {
    Object.freeze(x);
    Object.values(x as Record<string, unknown>).forEach(deepFreeze);
  }
  return x;
}

/** `{ negative: '?k' }` → `{ kind: 'negative', of: ['k'] }`. */
function toCondition(rec: Partial<Record<PreconditionKind, string | string[]>>): Condition {
  const kind = Object.keys(rec)[0] as PreconditionKind;
  const raw = rec[kind]!;
  const of = (Array.isArray(raw) ? raw : [raw]).map((t) => t.replace(/^\?/, ''));
  return ConditionSchema.parse({ kind, of });
}

function conditionsToSampler(vars: Record<string, Sort>, conds: Condition[]): Sampler {
  const domains: Sampler['domains'] = {};
  Object.entries(vars).forEach(([n, sort]) => (domains[n] = { sort, conds: [] }));
  const distinct: string[][] = [];
  conds.forEach((c) => {
    if (c.kind === 'distinct') distinct.push(c.of);
    else if (c.kind !== 'defined_on' && c.kind !== 'zero') domains[c.of[0]!]?.conds.push(c.kind);
  });
  return { domains, distinct };
}

function semanticIssue(
  r: SemanticResult,
  failCode: RuleIssueCode,
  untestableCode: RuleIssueCode,
): { code: RuleIssueCode; detail?: string } | null {
  if (r.verdict === 'holds') return null;
  if (r.verdict === 'untestable') return { code: untestableCode };
  return { code: failCode, detail: JSON.stringify(r.env) };
}

/** Vérifie une règle déjà conforme au schéma ; renvoie la règle chargée ou ses défauts. */
export function checkRuleSpec(
  spec: RuleSpec,
  packQid: string,
): { rule?: LoadedRule; issues: RuleIssue[] } {
  const issues: RuleIssue[] = [];
  const push = (code: RuleIssueCode, path?: string, detail?: string) =>
    issues.push({
      code,
      rule: spec.id,
      ...(path && { path }),
      ...(detail !== undefined && { detail }),
    });

  // --- sortes : le lot 1 ne sait éprouver que des scalaires réels
  Object.entries(spec.vars).forEach(([n, s]) => {
    if (!SCALAR_REAL_SORTS.includes(s)) push('sort_not_supported', `vars.${n}`, s);
  });

  // --- formes
  const parsed: Record<string, ParsedText> = {};
  Object.entries(spec.forms).forEach(([key, text]) => {
    const r = parseForm(text);
    if (!r.ok) push(`form_${r.code}` as RuleIssueCode, `forms.${key}`, r.detail);
    else parsed[key] = r.parsed;
  });
  const declared = new Set(Object.keys(spec.vars));
  const used = new Set<string>();
  Object.entries(parsed).forEach(([key, p]) =>
    p.vars.forEach((v) => {
      used.add(v);
      if (!declared.has(v)) push('var_undeclared', `forms.${key}`, v);
    }),
  );
  if (Object.keys(parsed).length === Object.keys(spec.forms).length)
    declared.forEach((v) => used.has(v) || push('var_unused', `vars.${v}`, v));

  // --- cible et relations des formes
  let target: RuleTarget | undefined = spec.applies_to;
  if (spec.kind === 'identity') {
    if (target !== undefined && target !== 'expression')
      push('applies_to_mismatch', 'applies_to', target);
    target = 'expression';
  } else if (target === undefined) push('applies_to_missing', 'applies_to');
  else if (target === 'expression') push('applies_to_mismatch', 'applies_to', target);
  if (target) {
    Object.entries(parsed).forEach(([key, p]) => {
      const ok =
        target === 'expression'
          ? p.relation === 'none'
          : target === 'equation'
            ? p.relation === '='
            : INEQUALITY_RELATIONS.includes(p.relation);
      if (!ok) push('form_relation_mismatch', `forms.${key}`, p.relation);
    });
  }

  // --- sens d'application
  const pairs = new Set<string>();
  const usedForms = new Set<string>();
  spec.directions.forEach((d, i) => {
    const path = `directions.${i}`;
    if (!(d.from in spec.forms)) push('direction_unknown_form', path, d.from);
    if (!(d.to in spec.forms)) push('direction_unknown_form', path, d.to);
    if (d.from === d.to) push('direction_same_form', path, d.from);
    if (pairs.has(`${d.from}>${d.to}`)) push('direction_duplicate', path);
    pairs.add(`${d.from}>${d.to}`);
    usedForms.add(d.from);
    usedForms.add(d.to);
  });
  Object.keys(spec.forms).forEach((k) => usedForms.has(k) || push('form_unused', `forms.${k}`, k));

  // --- préconditions
  const preconditions: Condition[] = [];
  (spec.preconditions ?? []).forEach((rec, i) => {
    const c = toCondition(rec);
    c.of.forEach(
      (v) => declared.has(v) || push('precondition_var_undeclared', `preconditions.${i}`, v),
    );
    preconditions.push(c);
  });
  if (spec.on_unknown && !preconditions.length)
    push('on_unknown_without_preconditions', 'on_unknown');
  if (preconditions.length && !spec.on_unknown) push('on_unknown_missing', 'on_unknown');

  // --- effets
  const flip = (spec.effects ?? []).some((e) => e.flip_relation);
  if (spec.effects?.length && spec.kind === 'identity') push('effects_on_identity', 'effects');
  else if (flip && target !== 'inequality')
    push('effect_not_applicable', 'effects', 'flip_relation');
  if (target === 'inequality')
    spec.directions.forEach((d, i) => {
      const a = parsed[d.from]?.relation;
      const b = parsed[d.to]?.relation;
      if (a && b && b !== (flip ? flipRelation(a) : a))
        push('flip_inconsistent', `directions.${i}`, `${a} → ${b}`);
    });

  if (issues.length || !target) return { issues };

  // --- auto-test de la règle (chaque paire de formes, une fois par sens utile)
  const sampler = conditionsToSampler(spec.vars, preconditions);
  let tested = 0;
  const seen = new Set<string>();
  spec.directions.forEach((d, i) => {
    const key = spec.kind === 'implication' ? `${d.from}>${d.to}` : [d.from, d.to].sort().join('|');
    if (seen.has(key)) return;
    seen.add(key);
    const r = checkSemantics(
      spec.kind,
      target,
      parsed[d.from]!,
      parsed[d.to]!,
      sampler,
      `${spec.id}#${key}`,
    );
    const bad = semanticIssue(r, 'rule_unsound', 'rule_untestable');
    if (bad) push(bad.code, `directions.${i}`, bad.detail);
    else if (r.verdict === 'holds') tested += r.tested;
  });

  // --- exemples
  const examples: LoadedExample[] = [];
  spec.examples.forEach((ex, i) => {
    const path = `examples.${i}`;
    const f = parseExample(ex.from);
    const t = parseExample(ex.to);
    if (!f.ok || !t.ok) {
      push(
        'example_unparsable',
        path,
        !f.ok ? `${f.code}: ${f.detail}` : !t.ok ? `${t.code}: ${t.detail}` : '',
      );
      return;
    }
    const relOk = (p: ParsedText) =>
      target === 'expression'
        ? p.relation === 'none'
        : target === 'equation'
          ? p.relation === '='
          : INEQUALITY_RELATIONS.includes(p.relation);
    if (!relOk(f.parsed) || !relOk(t.parsed)) {
      push('example_relation_mismatch', path);
      return;
    }
    const free = [...new Set([...f.parsed.vars, ...t.parsed.vars])];
    const assume = (ex.assume ?? []).map(toCondition);
    assume.forEach((c) =>
      c.of.forEach((v) => free.includes(v) || push('example_assume_unknown_var', path, v)),
    );
    const vars = Object.fromEntries(free.map((v) => [v, 'real' as Sort]));
    const r = checkSemantics(
      spec.kind,
      target,
      f.parsed,
      t.parsed,
      conditionsToSampler(vars, assume),
      `${spec.id}#ex${i}`,
    );
    if (ex.expect === 'valid') {
      const bad = semanticIssue(r, 'example_failed', 'example_untestable');
      if (bad) push(bad.code, path, bad.detail);
    } else if (r.verdict === 'holds') push('example_not_refuted', path);
    else if (r.verdict === 'untestable') push('example_untestable', path);
    examples.push({ from: ex.from, to: ex.to, expect: ex.expect, assume });
  });

  if (issues.length) return { issues };

  const forms: Record<string, LoadedForm> = {};
  Object.entries(spec.forms).forEach(([key, text]) => {
    const p = parsed[key]!;
    forms[key] = { key, text, relation: p.relation, vars: p.vars };
  });
  const rule: LoadedRule = {
    qid: `${spec.id}@${spec.version}`,
    id: spec.id,
    version: spec.version,
    pack: packQid,
    kind: spec.kind,
    relation: RULE_KIND_RELATION[spec.kind],
    appliesTo: target,
    vars: { ...spec.vars },
    forms,
    directions: spec.directions.map((d) => ({
      from: d.from,
      to: d.to,
      goals: [...d.goals],
      introduces: parsed[d.to]!.vars.filter((v) => !parsed[d.from]!.vars.includes(v)),
    })),
    preconditions,
    onUnknown: spec.on_unknown ?? null,
    flipRelation: flip,
    cost: spec.cost ?? DEFAULT_COST,
    label: spec.label,
    ref: spec.ref ?? null,
    tags: [...(spec.tags ?? [])],
    levels: [...(spec.levels ?? [])],
    maxMatches: spec.limits?.max_matches ?? DEFAULT_MAX_MATCHES,
    examples,
    selfTest: { proof: 'sampled', tested },
  };
  // lot 2 : chaque exemple est rejoué par l'APPARIEMENT des formes (pas seulement par les valeurs)
  examples.forEach((ex, i) => {
    const verdict = recognizes(rule, ex);
    if (ex.expect === 'valid' && verdict === 'no') push('example_not_recognized', `examples.${i}`);
    if (ex.expect === 'invalid' && verdict === 'valid')
      push('example_wrongly_recognized', `examples.${i}`);
  });
  return issues.length ? { issues } : { issues, rule };
}

/**
 * La règle reconnaît-elle l'exemple ? 'valid' : appariement trouvé et préconditions
 * vraies ; 'maybe' : appariement trouvé, préconditions non décidées ; 'no' : aucun
 * appariement dont les préconditions ne soient pas fausses.
 */
function recognizes(rule: LoadedRule, ex: LoadedExample): 'valid' | 'maybe' | 'no' {
  const from = parseStatement(ex.from);
  const to = parseStatement(ex.to);
  if (!from.ok || !to.ok) return 'no';
  const facts = factsFrom(ex.assume);
  let best: 'valid' | 'maybe' | 'no' = 'no';
  for (const m of matchRuleStep(rule, from.s, to.s)) {
    const ds = m.conditions.map((c) => decide(c, facts));
    if (ds.includes('false')) continue;
    if (ds.every((d) => d === 'true')) return 'valid';
    best = 'maybe';
  }
  return best;
}

/** JSON (déjà parsé) d'un paquet → paquet chargé, gelé ; ou la liste complète des défauts. */
export function loadRulePack(input: unknown): LoadResult {
  const parsed = RulePackSchema.safeParse(input);
  if (!parsed.success)
    return {
      ok: false,
      issues: parsed.error.issues.map((i) => ({
        code: 'schema' as const,
        path: i.path.join('.'),
        detail: i.message,
      })),
    };
  const spec = parsed.data;
  const qid = `${spec.id}@${spec.version}`;
  const issues: RuleIssue[] = [];
  const rules: LoadedRule[] = [];
  const ids = new Set<string>();
  spec.rules.forEach((r, i) => {
    if (ids.has(r.id)) issues.push({ code: 'rule_duplicate', rule: r.id, path: `rules.${i}` });
    ids.add(r.id);
    const res = checkRuleSpec(r, qid);
    res.issues.forEach((iss) =>
      issues.push({ ...iss, path: `rules.${i}${iss.path ? '.' + iss.path : ''}` }),
    );
    if (res.rule) rules.push(res.rule);
  });
  if (issues.length) return { ok: false, issues };
  return {
    ok: true,
    pack: deepFreeze({ qid, id: spec.id, version: spec.version, label: spec.label ?? null, rules }),
  };
}
